import type { PropType } from "vue";

type Validator<T = any> = (val: T) => boolean;

type OptionalProp<T = any> = {
  type: PropType<T>;
  validator?: Validator<T>;
};

type OptionalPropWithDefault<T> = OptionalProp<T> & {
  default: T | (() => T);
};

type OptionalPropWithDefaultFn<T> = OptionalProp<T> & {
  default: () => T;
};

type RequiredProp<T> = Pick<OptionalProp<T>, "type" | "validator"> & {
  required: true;
};

class PropBuilder {
  public string<T extends string>(
    defaultValue?: undefined,
    validator?: Validator<T>
  ): OptionalProp<T>;
  public string<T>(defaultVal: T | (() => T), validator?: Validator<T>): OptionalPropWithDefault<T>;
  public string<T>(
    defaultVal?: T,
    validator?: Validator<T>
  ): OptionalProp<T> | OptionalPropWithDefault<T> {
    return {
      type: String as unknown as PropType<T>,
      ...(defaultVal ? { default: defaultVal } : {}),
      validator,
    };
  }

  public $string<T extends string>(validator?: (val: T) => boolean): RequiredProp<T> {
    return {
      type: String as unknown as PropType<T>,
      validator,
      required: true,
    };
  }

  public number<T extends number>(
    defaultValue?: undefined,
    validator?: Validator<T>
  ): OptionalProp<T>;
  public number<T>(defaultVal: T, validator?: Validator<T>): OptionalPropWithDefault<T>;
  public number<T>(
    defaultVal?: T,
    validator?: Validator<T>
  ): OptionalProp<T> | OptionalPropWithDefault<T> {
    return {
      type: Number as unknown as PropType<T>,
      ...(defaultVal != null ? { default: defaultVal } : {}),
      validator,
    };
  }

  public $number(validator?: (val: number) => boolean): RequiredProp<number> {
    return {
      type: Number as PropType<number>,
      validator,
      required: true,
    };
  }

  public boolean(
    defaultVal?: boolean | null,
    validator?: (val: boolean) => boolean
  ): OptionalProp<boolean> {
    return {
      type: Boolean as PropType<boolean>,
      ...(defaultVal !== undefined ? { default: defaultVal } : {}),
      validator,
    };
  }

  public object<T extends object>(
    defaultValue?: undefined,
    validator?: Validator<T>
  ): OptionalProp<T>;
  public object<T>(defaultVal: T, validator?: Validator<T>): OptionalPropWithDefault<T>;
  public object<T>(
    defaultVal?: T,
    validator?: Validator<T>
  ): OptionalProp<T> | OptionalPropWithDefaultFn<T> {
    return {
      type: Object as PropType<T>,
      default: () => defaultVal as T,
      validator,
    };
  }

  public $object<T>(validator?: (val: T) => boolean): RequiredProp<T> {
    return {
      type: Object as PropType<T>,
      validator,
      required: true,
    };
  }

  public func<T extends (...args: any[]) => any>(): OptionalProp<T> {
    return {
      type: Function as PropType<T>,
    };
  }

  public $func<T extends (...args: any[]) => any>(
    validator?: (val: T) => boolean
  ): RequiredProp<T> {
    return {
      type: Function as PropType<T>,
      validator,
      required: true,
    };
  }

  public array<T extends any[]>(
    defaultValue?: undefined,
    validator?: Validator<T>
  ): OptionalProp<T>;
  public array<T>(defaultVal: T, validator?: Validator<T>): OptionalPropWithDefault<T>;
  public array<T>(
    defaultVal?: T,
    validator?: Validator<T>
  ): OptionalProp<T> | OptionalPropWithDefaultFn<T> {
    return {
      type: Array as unknown as PropType<T>,
      default: () => defaultVal as T,
      validator,
    };
  }

  public $array<T>(validator?: (val: T[]) => boolean): RequiredProp<T[]> {
    return {
      type: Array as PropType<T[]>,
      validator,
      required: true,
    };
  }

  public custom<T>(
    types: PropType<T>,
    defaultVal?: T,
    validator?: Validator<T>
  ): OptionalProp<T> | OptionalPropWithDefaultFn<T> {
    return {
      type: types,
      default: () => defaultVal as T,
      validator,
    };
  }

  public $custom<T>(types: PropType<T>, validator?: (val: T) => boolean): RequiredProp<T> {
    return {
      type: types,
      validator,
      required: true,
    };
  }
}

export const p = new PropBuilder();
